/**
 * Fishionist poster view — how a generated poster is drawn, in one place.
 *
 * Extracted verbatim from fishionist-preview-modal-v2.js so the product page
 * and the homepage Poster Builder cannot drift apart. The preview is the part
 * of this store that keeps being redesigned; two copies of it would mean the
 * customer sees one poster on the product page and a different one in the
 * modal, which is exactly the bug this file exists to make impossible.
 *
 * Pure rendering. Nothing here reads the variant picker, the cart, or any
 * global state: the caller passes in what it already knows. That is the only
 * change from the original — the product page reads Style/Size from
 * <variant-selects>, the builder reads them from its own buttons, and both hand
 * the answer to the same drawing code.
 *
 *   FshPosterView.fillArt(box, art)
 *   FshPosterView.buildRoom(container, opts)
 *   FshPosterView.posterScale(sizeLabel, allSizeLabels)
 *   FshPosterView.isFramedValue(value)
 *   FshPosterView.longEdge(sizeLabel)
 *   FshPosterView.readRooms()
 *   FshPosterView.readBaseFrameW(fallback)
 */
(function () {
  'use strict';

  if (window.FshPosterView) return;

  var DEFAULT_PLACEMENT = { scale: 0.85, xPct: 50, yPct: 50 };

  function el(tag, cls) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    return n;
  }

  // "Poster (With Frame - Ready to Hang)" is framed, "Poster (Without Frame)"
  // is not. Matching on the words rather than a hard-coded string keeps working
  // if the option labels are reworded.
  function isFramedValue(v) {
    return !!v && !/without/i.test(v) && /frame/i.test(v);
  }

  // All three sizes are 2:3, so the long edge alone gives an exact ratio.
  function longEdge(sizeLabel) {
    var m = String(sizeLabel || '').match(/(\d+)\s*x\s*(\d+)/i);
    if (!m) return 0;
    return Math.max(parseInt(m[1], 10), parseInt(m[2], 10));
  }

  // Scale against the largest size the product offers rather than a hard-coded
  // 30in, so this keeps working if the size range ever changes.
  function posterScale(sizeLabel, allSizeLabels) {
    var current = longEdge(sizeLabel);
    if (!current) return 1;
    var all = (allSizeLabels || []).map(longEdge);
    var max = Math.max.apply(null, all.concat([current]));
    return max ? current / max : 1;
  }

  /**
   * Draw the artwork into `box`.
   *
   * art = {
   *   url        watermarked preview URL — the clean print file must never
   *              reach the DOM or it can simply be right-clicked
   *   mode       'plain' | 'background'
   *   composed   a background poster is two layers until /compose runs
   *   bgUrl      the background layer, when not composed
   *   placement  { scale, xPct, yPct } of the subject over that background
   *   alt        optional alt text
   * }
   */
  function fillArt(box, art) {
    if (!box) return;
    art = art || {};

    box.textContent = '';
    box.classList.remove('is-layered');
    box.style.backgroundImage = '';
    if (!art.url) return;

    var img = el('img');
    img.alt = art.alt == null ? 'Your custom poster' : art.alt;

    if (art.mode === 'background' && !art.composed) {
      box.classList.add('is-layered');
      if (art.bgUrl) box.style.backgroundImage = 'url("' + art.bgUrl + '")';
      var p = art.placement || DEFAULT_PLACEMENT;
      img.style.height = Math.max(5, Math.min(100, p.scale * 100)) + '%';
      img.style.left = p.xPct + '%';
      img.style.top = p.yPct + '%';
    }

    img.src = art.url;
    box.appendChild(img);
  }

  /**
   * Draw the poster hanging on a wall photo, into `container`.
   *
   * opts = {
   *   art        same shape fillArt takes
   *   framed     boolean
   *   scale      0..1 from posterScale()
   *   room       { label, url, x, y, h } from readRooms()
   *   baseFrameW frame thickness at the largest size, from readBaseFrameW()
   * }
   */
  function buildRoom(container, opts) {
    if (!container) return;
    opts = opts || {};
    var cfg = opts.room;
    if (!cfg) return;

    container.textContent = '';

    var wrap = el('div', 'fsh-room');
    var photo = el('img', 'fsh-room__photo');
    photo.alt = cfg.label || 'Room';
    photo.src = cfg.url;
    wrap.appendChild(photo);

    var poster = el('div', 'fsh-frame fsh-room__poster');
    var scale = opts.scale == null ? 1 : opts.scale;
    poster.style.setProperty('--fsh-room-x', cfg.x);
    poster.style.setProperty('--fsh-room-y', cfg.y);
    poster.style.setProperty('--fsh-room-h', cfg.h);
    poster.style.setProperty('--fsh-poster-scale', scale);
    // The moulding is a physical object: a 12x18 print gets the same thickness
    // of wood as a 20x30. Frame width is a percentage of the poster's own width,
    // so it has to be divided back out by the scale to stay constant on the wall.
    poster.style.setProperty(
      '--fsh-frame-w',
      ((opts.baseFrameW || 3.75) / (scale || 1)).toFixed(3)
    );
    poster.classList.toggle('is-framed', !!opts.framed);

    var artBox = el('div', 'fsh-modal__art fsh-room__art');
    fillArt(artBox, opts.art);
    poster.appendChild(artBox);
    wrap.appendChild(poster);
    container.appendChild(wrap);
  }

  // Room config as JSON, rendered by snippets/fsh-rooms-json.liquid. Sizes are
  // the poster's height as a percentage of the photo, at the largest size.
  function readRooms(selector) {
    var tag = document.querySelector(selector || '[data-fsh-rooms]');
    if (!tag) return [];
    try {
      var list = JSON.parse(tag.textContent);
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  // The frame thickness the theme setting produced, read back off the CSS so
  // the room scene can divide it out by the poster scale.
  function readBaseFrameW(fallback) {
    var probe = document.querySelector('.fsh-frame');
    if (!probe) return fallback || 3.75;
    var w = parseFloat(getComputedStyle(probe).getPropertyValue('--fsh-frame-w'));
    return w || fallback || 3.75;
  }

  window.FshPosterView = {
    DEFAULT_PLACEMENT: DEFAULT_PLACEMENT,
    el: el,
    fillArt: fillArt,
    buildRoom: buildRoom,
    posterScale: posterScale,
    isFramedValue: isFramedValue,
    longEdge: longEdge,
    readRooms: readRooms,
    readBaseFrameW: readBaseFrameW
  };
})();
